<template>
    <div class="vue-search-estates-form">
        <ul class="nav nav-tabs d-flex" v-if="config.showTabs && config.estateTypes.length > 1">
            <li class="nav-item flex-grow-1" v-for="type in config.estateTypes" :key="type.value">
                <a class="nav-link cursor-pointer"
                   :class="[
                       {active: formValues.estateType === type.value},
                       config.monochromeTypeIcons ? 'estate-type-card' : '',
                   ]"
                   @click="switchEstateType(type.value)">
                    <span v-if="config.monochromeTypeIcons"
                          class="estate-type-card-icon"
                          :class="'estate-avatar-' + type.value">
                        <i class="fas" :class="type.icon" aria-hidden="true"></i>
                    </span>
                    <svg v-else aria-hidden="true" focusable="false" width="45" height="45">
                        <use :href="basePath + '/images/icons-svg/estateDatabaseIcons.svg#' + type.value"></use>
                    </svg>
                    {{ type.label }}
                </a>
            </li>
        </ul>
        <div :class="config.showTabs ? 'vue-search-form-body pt-4 px-3 pb-1 shadow' : ''">
            <form @submit.prevent="submit" novalidate>
                <div class="row">
                    <div class="col-lg-6">
                        <!-- Date range -->
                        <div class="row">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Období od</label>
                                    <div class="input-group">
                                        <input type="text"
                                               class="form-control"
                                               :class="{'is-invalid': errors.minDate}"
                                               ref="minDate">
                                        <span class="input-group-text"><i class="fas fa-calendar-alt"></i></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minDate">{{ errors.minDate }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Období do</label>
                                    <div class="input-group">
                                        <input type="text"
                                               class="form-control"
                                               :class="{'is-invalid': errors.maxDate}"
                                               ref="maxDate">
                                        <span class="input-group-text"><i class="fas fa-calendar-alt"></i></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxDate">{{ errors.maxDate }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Price range -->
                        <div class="row">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Cena od</label>
                                    <div class="input-group">
                                        <input type="text" class="form-control"
                                               :class="{'is-invalid': errors.minPrice}"
                                               :value="formatPriceWithUnit(formValues.minPrice)"
                                               @input="onPriceInput($event, 'minPrice')">
                                        <button class="btn btn-toggle-unit" type="button"
                                                @click="formValues.priceUnit = formValues.priceUnit === 'total' ? 'sqm' : 'total'"
                                                title="Klikněte pro změnu jednotky">
                                            <i class="fas fa-exchange-alt"></i>
                                        </button>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minPrice">{{ errors.minPrice }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Cena do</label>
                                    <div class="input-group">
                                        <input type="text" class="form-control"
                                               :class="{'is-invalid': errors.maxPrice}"
                                               :value="formatPriceWithUnit(formValues.maxPrice)"
                                               @input="onPriceInput($event, 'maxPrice')">
                                        <button class="btn btn-toggle-unit" type="button"
                                                @click="formValues.priceUnit = formValues.priceUnit === 'total' ? 'sqm' : 'total'"
                                                title="Klikněte pro změnu jednotky">
                                            <i class="fas fa-exchange-alt"></i>
                                        </button>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxPrice">{{ errors.maxPrice }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-6">
                        <!-- Location type -->
                        <div class="row">
                            <div class="col-12 mb-3">
                                <label class="form-label d-block">Typ hledané lokality</label>
                                <div class="btn-group w-100" role="group">
                                    <template v-for="lt in locationTypeOptions" :key="lt.value">
                                        <input type="radio"
                                               class="btn-check"
                                               :id="'locationType-' + lt.value"
                                               :value="lt.value"
                                               v-model="formValues.locationType"
                                               autocomplete="off">
                                        <label class="btn btn-toggle"
                                               :for="'locationType-' + lt.value">
                                            <i :class="lt.icon" class="me-3"></i>{{ lt.label }}
                                        </label>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <!-- Location -->
                        <div class="row" v-show="formValues.locationType === 'location'">
                            <div class="col-md-8">
                                <div class="mb-3">
                                    <label class="form-label">Lokalita hledané nemovitosti</label>
                                    <LocationSearchControl :value="formValues.location" @input="formValues.location = $event" placeholder=""/>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-3">
                                    <label class="form-label">Hledat v okolí</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="0.1" min="0"
                                               :class="{'is-invalid': errors.radius}"
                                               v-model="formValues.radius">
                                        <span class="input-group-text">km</span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.radius">{{ errors.radius }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Cadastral units map -->
                <div class="row" v-show="formValues.locationType === 'cadastral_units'">
                    <div class="col-12">
                        <div class="w-100 mb-3" style="height:500px;">
                            <CadastralUnitsMapField
                                name="cadastralUnits"
                                :center="config.cadastralUnitsCenter"
                                :loadPointsUrl="config.cadastralUnitsLoadUrl"
                                :value="formValues.cadastralUnits"
                                :useGeolocation="formValues.cadastralUnits === ''"
                                @selection-changed="onCadastralUnitsChanged"/>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-6">
                        <!-- Floor area -->
                        <div class="row" v-if="isVisible('floorArea')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Podlahová plocha od</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.minFloorArea}"
                                               v-model="formValues.minFloorArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minFloorArea">{{ errors.minFloorArea }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Podlahová plocha do</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.maxFloorArea}"
                                               v-model="formValues.maxFloorArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxFloorArea">{{ errors.maxFloorArea }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Built-up area (left column: all except garage) -->
                        <div class="row" v-if="isVisible('builtUpArea') && (isVisible('buildingType') || isVisible('floors'))">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Zastavěná plocha od</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.minBuiltUpArea}"
                                               v-model="formValues.minBuiltUpArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minBuiltUpArea">{{ errors.minBuiltUpArea }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Zastavěná plocha do</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.maxBuiltUpArea}"
                                               v-model="formValues.maxBuiltUpArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxBuiltUpArea">{{ errors.maxBuiltUpArea }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Land area (left column: land type only, where builtUpArea is not visible) -->
                        <div class="row" v-if="isVisible('landArea') && !isVisible('builtUpArea')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Plocha pozemku od</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.minLandArea}"
                                               v-model="formValues.minLandArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minLandArea">{{ errors.minLandArea }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Plocha pozemku do</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.maxLandArea}"
                                               v-model="formValues.maxLandArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxLandArea">{{ errors.maxLandArea }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Floor range (flat only) -->
                        <div class="row" v-if="isVisible('floor')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Podlaží od</label>
                                    <input type="number"
                                           class="form-control"
                                           :class="{'is-invalid': errors.minFloor}"
                                           v-model="formValues.minFloor">
                                    <div class="invalid-feedback" v-if="errors.minFloor">{{ errors.minFloor }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Podlaží do</label>
                                    <input type="number"
                                           class="form-control"
                                           :class="{'is-invalid': errors.maxFloor}"
                                           v-model="formValues.maxFloor">
                                    <div class="invalid-feedback" v-if="errors.maxFloor">{{ errors.maxFloor }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Description -->
                        <div class="row">
                            <div class="col-12">
                                <div class="mb-3">
                                    <label class="form-label">Popis nemovitosti obsahuje</label>
                                    <input type="text" class="form-control" v-model="formValues.description">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-6">
                        <!-- Estate state + Construction -->
                        <div class="row" v-if="isVisible('estateState')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Stav objektu</label>
                                    <v-select
                                        :options="estateStateOptions"
                                        v-model="selectedEstateStates"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Konstrukce</label>
                                    <v-select
                                        :options="constructionOptions"
                                        v-model="selectedConstructions"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                        </div>

                        <!-- Built-up area (right column: garage only) -->
                        <div class="row" v-if="isVisible('builtUpArea') && !isVisible('buildingType') && !isVisible('floors')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Zastavěná plocha od</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.minBuiltUpArea}"
                                               v-model="formValues.minBuiltUpArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minBuiltUpArea">{{ errors.minBuiltUpArea }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Zastavěná plocha do</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.maxBuiltUpArea}"
                                               v-model="formValues.maxBuiltUpArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxBuiltUpArea">{{ errors.maxBuiltUpArea }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Ownership + Room layout (flat only) -->
                        <div class="row" v-if="isVisible('roomLayout')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Vlastnictví</label>
                                    <v-select
                                        :options="ownershipOptions"
                                        v-model="selectedOwnerships"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Dispozice</label>
                                    <v-select
                                        :options="roomLayoutOptions"
                                        v-model="selectedRoomLayouts"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                        </div>

                        <!-- Building type + Floors -->
                        <div class="row" v-if="isVisible('buildingType')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Typ stavby</label>
                                    <v-select
                                        :options="buildingTypeOptions"
                                        v-model="selectedBuildingTypes"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                            <div class="col-md-6" v-if="isVisible('floors')">
                                <div class="mb-3">
                                    <label class="form-label">Počet nadzemních podlaží</label>
                                    <input type="number"
                                           class="form-control" min="1"
                                           :class="{'is-invalid': errors.floors}"
                                           v-model="formValues.floors">
                                    <div class="invalid-feedback" v-if="errors.floors">{{ errors.floors }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Land area (right column: family_house, recreational) -->
                        <div class="row" v-if="isVisible('landArea') && isVisible('builtUpArea')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Plocha pozemku od</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.minLandArea}"
                                               v-model="formValues.minLandArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.minLandArea">{{ errors.minLandArea }}</div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Plocha pozemku do</label>
                                    <div class="input-group">
                                        <input type="number"
                                               class="form-control" step="1" min="0"
                                               :class="{'is-invalid': errors.maxLandArea}"
                                               v-model="formValues.maxLandArea">
                                        <span class="input-group-text">m<sup>2</sup></span>
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="errors.maxLandArea">{{ errors.maxLandArea }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Land type (land only) -->
                        <div class="row" v-if="isVisible('landType')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Typ pozemku</label>
                                    <v-select
                                        :options="landTypeOptions"
                                        v-model="selectedLandTypes"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                        </div>

                        <!-- Commercial type + Floors (commercial only) -->
                        <div class="row" v-if="isVisible('commercialType')">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label">Druh komerční nemovitosti</label>
                                    <v-select
                                        :options="commercialTypeOptions"
                                        v-model="selectedCommercialTypes"
                                        multiple
                                        :searchable="false"/>
                                </div>
                            </div>
                            <div class="col-md-6" v-if="isVisible('floors')">
                                <div class="mb-3">
                                    <label class="form-label">Počet nadzemních podlaží</label>
                                    <input type="number"
                                           class="form-control" min="1"
                                           :class="{'is-invalid': errors.floors}"
                                           v-model="formValues.floors">
                                    <div class="invalid-feedback" v-if="errors.floors">{{ errors.floors }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bottom row: offer type, checkboxes, submit -->
                <div class="row">
                    <div class="col-md-3 pt-1 mb-n1" v-if="!config.fixedOfferType">
                        <div class="form-check form-check-inline" v-for="ot in config.offerTypes" :key="ot.value">
                            <input class="form-check-input" type="radio"
                                   :id="'offerType-' + ot.value"
                                   :value="ot.value"
                                   v-model="formValues.offerType">
                            <label class="form-check-label form-label ms-2" :for="'offerType-' + ot.value">
                                {{ ot.label }}
                            </label>
                        </div>
                    </div>
                    <div class="col-md-3 pt-1 mb-n1" v-if="showPurchaseContract">
                        <div class="mb-3 form-check">
                            <input class="form-check-input" type="checkbox" id="purchaseContract" v-model="formValues.purchaseContract">
                            <label class="form-label ms-2" for="purchaseContract">Jen s kupními smlouvami</label>
                        </div>
                    </div>
                    <div class="col-md-3 pt-1 mb-n1" v-if="config.withNotActive">
                        <div class="mb-3 form-check">
                            <input class="form-check-input" type="checkbox" id="notActive" v-model="formValues.notActive">
                            <label class="form-label ms-2" for="notActive">Jen neaktivní nemovitosti</label>
                        </div>
                    </div>
                    <div class="col text-end mb-n1">
                        <div class="mb-3">
                            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                                Vyhledat nemovitosti
                            </button>
                        </div>
                    </div>
                </div>
                <div class="alert alert-danger" v-if="submitError">{{ submitError }}</div>
            </form>
        </div>
    </div>
</template>

<script lang="ts">
import LocationSearchControl from '@/components/common/LocationSearchControl.vue';
import {DATE_FORMAT, i18n as pikadayI18n} from '@/components/DatePicker';
import CadastralUnitsMapField from '@/components/maps/CadastralUnitsMapField.vue';
import {
    BuildingType,
    CommercialType,
    ConstructionType,
    createDefaultValues,
    EnumOption,
    EstateState,
    EstateType,
    isFieldVisible,
    LandType,
    LocationType,
    OfferType,
    Ownership,
    PriceUnit,
    RoomLayout,
    SearchFormConfig,
    SearchFormValues,
    validate,
    ValidationErrors,
} from '@/model/estateDatabase/searchForm';
import * as Sentry from '@sentry/vue';
import naja from 'naja';
import Pikaday from 'pikaday';
import {computed, defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, reactive, ref} from 'vue';
import vSelect from 'vue-select';

interface SelectOption<T extends string> {
    label: string;
    value: T;
}

export default defineComponent({
    components: {LocationSearchControl, CadastralUnitsMapField, vSelect},
    props: {
        config: {
            type: Object as PropType<SearchFormConfig>,
            required: true,
        },
        basePath: {
            type: String,
            default: '',
        },
    },
    setup(props) {
        const minDate = ref<HTMLInputElement | null>(null);
        const maxDate = ref<HTMLInputElement | null>(null);

        const formValues = reactive<SearchFormValues>(createDefaultValues(props.config));
        const isSubmitting = ref(false);
        const submitError = ref('');
        const errors = ref<ValidationErrors>({});

        let minDatePicker: Pikaday | null = null;
        let maxDatePicker: Pikaday | null = null;

        const selectedEstateStates = ref<Array<SelectOption<EstateState>>>([]);
        const selectedConstructions = ref<Array<SelectOption<ConstructionType>>>([]);
        const selectedBuildingTypes = ref<Array<SelectOption<BuildingType>>>([]);
        const selectedRoomLayouts = ref<Array<SelectOption<RoomLayout>>>([]);
        const selectedOwnerships = ref<Array<SelectOption<Ownership>>>([]);
        const selectedCommercialTypes = ref<Array<SelectOption<CommercialType>>>([]);
        const selectedLandTypes = ref<Array<SelectOption<LandType>>>([]);

        const locationTypeOptions = computed((): Array<EnumOption<LocationType> & {icon: string}> => {
            return [
                {value: 'location', label: 'Přesná lokalita + radius', icon: 'fas fa-map-marker-alt'},
                {value: 'cadastral_units', label: 'Katastrální území', icon: 'fas fa-map'},
            ];
        });

        const estateStateOptions = computed((): Array<SelectOption<EstateState>> => {
            return props.config.estateStates.map((o) => ({label: o.label, value: o.value}));
        });

        const constructionOptions = computed((): Array<SelectOption<ConstructionType>> => {
            return props.config.constructions.map((o) => ({label: o.label, value: o.value}));
        });

        const buildingTypeOptions = computed((): Array<SelectOption<BuildingType>> => {
            return props.config.buildingTypes.map((o) => ({label: o.label, value: o.value}));
        });

        const roomLayoutOptions = computed((): Array<SelectOption<RoomLayout>> => {
            return props.config.roomLayouts.map((o) => ({label: o.label, value: o.value}));
        });

        const ownershipOptions = computed((): Array<SelectOption<Ownership>> => {
            return props.config.ownerships.map((o) => ({label: o.label, value: o.value}));
        });

        const commercialTypeOptions = computed((): Array<SelectOption<CommercialType>> => {
            return props.config.commercialTypes.map((o) => ({label: o.label, value: o.value}));
        });

        const landTypeOptions = computed((): Array<SelectOption<LandType>> => {
            return props.config.landTypes.map((o) => ({label: o.label, value: o.value}));
        });

        const showPurchaseContract = computed((): boolean => {
            return !!props.config.withPurchaseContract && props.config.fixedOfferType !== 'lease';
        });

        function syncSelectionsFromValues(): void {
            selectedEstateStates.value = estateStateOptions.value
                .filter((o) => formValues.estateState.includes(o.value));
            selectedConstructions.value = constructionOptions.value
                .filter((o) => formValues.construction.includes(o.value));
            selectedBuildingTypes.value = buildingTypeOptions.value
                .filter((o) => formValues.buildingType.includes(o.value));
            selectedRoomLayouts.value = roomLayoutOptions.value
                .filter((o) => formValues.roomLayout.includes(o.value));
            selectedOwnerships.value = ownershipOptions.value
                .filter((o) => formValues.ownership.includes(o.value));
            selectedCommercialTypes.value = commercialTypeOptions.value
                .filter((o) => formValues.commercialType.includes(o.value));
            selectedLandTypes.value = landTypeOptions.value
                .filter((o) => formValues.landType.includes(o.value));
        }

        function syncValuesFromSelections(): void {
            formValues.estateState = selectedEstateStates.value.map((o) => o.value);
            formValues.construction = selectedConstructions.value.map((o) => o.value);
            formValues.buildingType = selectedBuildingTypes.value.map((o) => o.value);
            formValues.roomLayout = selectedRoomLayouts.value.map((o) => o.value);
            formValues.ownership = selectedOwnerships.value.map((o) => o.value);
            formValues.commercialType = selectedCommercialTypes.value.map((o) => o.value);
            formValues.landType = selectedLandTypes.value.map((o) => o.value);
        }

        function syncDatesFromDom(): void {
            if (minDate.value) {
                formValues.minDate = minDate.value.value;
            }
            if (maxDate.value) {
                formValues.maxDate = maxDate.value.value;
            }
        }

        // created logic
        syncSelectionsFromValues();

        function isVisible(fieldGroup: string): boolean {
            return isFieldVisible(fieldGroup, formValues.estateType);
        }

        function switchEstateType(type: EstateType): void {
            syncValuesFromSelections();
            syncDatesFromDom();
            formValues.estateType = type;
            errors.value = {};
        }

        function onCadastralUnitsChanged(selectedIds: string[]): void {
            formValues.cadastralUnits = selectedIds.join(',');
        }

        function formatPriceWithUnit(value: string): string {
            const digits = value.replace(/\s/g, '');
            if (digits === '') {
                return '';
            }
            const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            const unit = formValues.priceUnit === 'sqm' ? ' Kč/m²' : ' Kč';
            return formatted + unit;
        }

        function onPriceInput(event: Event, field: 'minPrice' | 'maxPrice'): void {
            const input = event.target as HTMLInputElement;
            const cursorPos = input.selectionStart ?? 0;
            const raw = input.value.replace(/[^0-9]/g, '');
            formValues[field] = raw;

            nextTick(() => {
                const formatted = formatPriceWithUnit(raw);
                input.value = formatted;

                const textBeforeCursor = input.value.substring(0, cursorPos);
                const digitsBeforeCursor = (textBeforeCursor.match(/[0-9]/g) || []).length;
                let newPos = 0;
                let digitCount = 0;
                for (let i = 0; i < formatted.length; i++) {
                    if (/[0-9]/.test(formatted[i])) {
                        digitCount++;
                    }
                    if (digitCount >= digitsBeforeCursor) {
                        newPos = i + 1;
                        break;
                    }
                }
                if (digitsBeforeCursor === 0) {
                    newPos = 0;
                }
                input.setSelectionRange(newPos, newPos);
            });
        }

        function buildRequestBody(): Record<string, unknown> {
            const v = formValues;
            const visible = (field: string): boolean => isFieldVisible(field, v.estateType);

            return {
                estateType: v.estateType,
                offerType: v.offerType,
                minDate: v.minDate,
                maxDate: v.maxDate,
                minPrice: v.minPrice !== ''
                    ? {amount: parseFloat(String(v.minPrice).replace(/\s/g, '')), currency: 'CZK'} : null,
                maxPrice: v.maxPrice !== ''
                    ? {amount: parseFloat(String(v.maxPrice).replace(/\s/g, '')), currency: 'CZK'} : null,
                usePricePerMeter: v.priceUnit === 'sqm',
                locationType: v.locationType,
                location: v.location,
                radius: v.radius !== '' ? parseFloat(v.radius) : null,
                cadastralUnits: v.cadastralUnits
                    ? v.cadastralUnits.split(',').filter((id: string) => id !== '') : [],
                minFloorArea: visible('floorArea') && v.minFloorArea !== ''
                    ? parseInt(v.minFloorArea, 10) : null,
                maxFloorArea: visible('floorArea') && v.maxFloorArea !== ''
                    ? parseInt(v.maxFloorArea, 10) : null,
                minBuiltUpArea: visible('builtUpArea') && v.minBuiltUpArea !== ''
                    ? parseInt(v.minBuiltUpArea, 10) : null,
                maxBuiltUpArea: visible('builtUpArea') && v.maxBuiltUpArea !== ''
                    ? parseInt(v.maxBuiltUpArea, 10) : null,
                minLandArea: visible('landArea') && v.minLandArea !== ''
                    ? parseInt(v.minLandArea, 10) : null,
                maxLandArea: visible('landArea') && v.maxLandArea !== ''
                    ? parseInt(v.maxLandArea, 10) : null,
                minFloor: visible('floor') && v.minFloor !== '' ? parseInt(v.minFloor, 10) : null,
                maxFloor: visible('floor') && v.maxFloor !== '' ? parseInt(v.maxFloor, 10) : null,
                floors: visible('floors') && v.floors !== '' ? parseInt(v.floors, 10) : null,
                estateState: visible('estateState') ? v.estateState : [],
                construction: visible('construction') ? v.construction : [],
                buildingType: visible('buildingType') ? v.buildingType : [],
                roomLayout: visible('roomLayout') ? v.roomLayout : [],
                ownership: visible('ownership') ? v.ownership : [],
                commercialType: visible('commercialType') ? v.commercialType : [],
                landType: visible('landType') ? v.landType : [],
                description: v.description || null,
                purchaseContract: v.purchaseContract,
                notActive: v.notActive,
                userSourceOnly: props.config.forceUserSourceOnly || v.userSourceOnly,
                favoriteOnly: props.config.forceFavoriteOnly || v.favoriteOnly,
                withoutDateOfSaleOnly: v.withoutDateOfSaleOnly,
            };
        }

        async function submit(): Promise<void> {
            if (isSubmitting.value) {
                return;
            }

            syncValuesFromSelections();
            syncDatesFromDom();
            errors.value = validate(formValues);

            if (Object.keys(errors.value).length > 0) {
                return;
            }

            isSubmitting.value = true;
            submitError.value = '';

            try {
                const response = await fetch(props.config.submitUrl, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(buildRequestBody()),
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();

                if (props.config.afterSubmitUrl) {
                    const url = props.config.afterSubmitUrl.replace(
                        '__SEARCH_REQUEST_ID__', encodeURIComponent(data.searchRequestId),
                    );
                    await naja.makeRequest('GET', url, null, {history: false});
                    isSubmitting.value = false;
                } else {
                    const url = new URL(window.location.href);
                    url.searchParams.set('searchRequestId', data.searchRequestId);
                    window.location.href = url.toString();
                }
            } catch (error) {
                Sentry.captureException(error);
                isSubmitting.value = false;
                submitError.value = 'Při odesílání formuláře došlo k chybě. Zkuste to prosím znovu.';
            }
        }

        onMounted(() => {
            minDatePicker = new Pikaday({
                field: minDate.value as HTMLElement,
                firstDay: 1,
                format: DATE_FORMAT,
                i18n: pikadayI18n,
                onSelect: () => {
                    formValues.minDate = (minDate.value as HTMLInputElement).value;
                },
            });

            maxDatePicker = new Pikaday({
                field: maxDate.value as HTMLElement,
                firstDay: 1,
                format: DATE_FORMAT,
                i18n: pikadayI18n,
                onSelect: () => {
                    formValues.maxDate = (maxDate.value as HTMLInputElement).value;
                },
            });

            if (formValues.minDate) {
                minDatePicker.setDate(formValues.minDate, true);
            }
            if (formValues.maxDate) {
                maxDatePicker.setDate(formValues.maxDate, true);
            }
        });

        onBeforeUnmount(() => {
            minDatePicker?.destroy();
            maxDatePicker?.destroy();
        });

        return {
            minDate, maxDate,
            formValues, isSubmitting, submitError, errors,
            selectedEstateStates, selectedConstructions, selectedBuildingTypes,
            selectedRoomLayouts, selectedOwnerships, selectedCommercialTypes, selectedLandTypes,
            locationTypeOptions, estateStateOptions, constructionOptions, buildingTypeOptions,
            roomLayoutOptions, ownershipOptions, commercialTypeOptions, landTypeOptions,
            showPurchaseContract,
            isVisible, switchEstateType, onCadastralUnitsChanged,
            formatPriceWithUnit, onPriceInput, submit,
        };
    },
});
</script>
